// Implements: F-RPT-005, F-UIX-018, F-UIX-010
/**
 * Mockup v3 P9 Consumo "Costo por modelo y flujo" for an organization's admin: this month so far
 * through `GET /reports/organizations/{org}/costs`, by model or by flow version.
 *
 * Mounted only for an organization target. The read is cached by organization and window
 * (`orgCostsKey`, checklist 14); the toggle only changes what is drawn from the one answer, so
 * switching asks nothing. The day the month is counted from is handed in (checklist 16).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { orgCostsKey } from "../../../shared/api/queryKeys";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { monthToDate } from "../../../shared/ui/reportWindows";
import type { CostReportView } from "../../../types/reports";
import { fetchOrgCosts } from "../api/orgReportsApi";
import { CostBreakdown } from "../components/CostBreakdown";
import type { CostGrouping } from "../components/CostBreakdown";

export interface CostBreakdownContainerProps {
  readonly organizationId: string;
  /** The day "this month" ends on. */
  readonly today: Date;
}

export function CostBreakdownContainer({ organizationId, today }: CostBreakdownContainerProps): ReactElement {
  const [by, setBy] = useState<CostGrouping>("model");
  const window = monthToDate(today);
  const costs = useQuery<CostReportView, ApiError>({
    queryKey: orgCostsKey(organizationId, window.from, window.to),
    queryFn: ({ signal }) => fetchOrgCosts(organizationId, window, signal),
  });
  if (costs.isError) {
    return (
      <ErrorState
        headingLevel={3}
        error={asApiError(costs.error)}
        onRetry={() => {
          void costs.refetch();
        }}
      />
    );
  }
  if (costs.data === undefined) return <TableSkeleton />;
  return <CostBreakdown report={costs.data} by={by} onBy={setBy} />;
}
